import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import LoadingProgress from '../components/LoadingProgress'

import {
  fetchSupportTickets,
  fetchSupportTicket,
  closeTicketSupport,
  reviewTicketSupport,
  resolveTicketSupport,
  incidenceTicketSupport,
  repairTicketSupport,
  deleteSupportTicket
} from '../actions/supporttickets'

import ErrorMessage from '../components/ErrorMessage'
import SupportTicketsFilterForm from '../forms/SupportTicketsFilterForm'

import '../styles/SupportTicketAdminView.css'

const STATUS_LABELS = {
  OPEN: 'Abierto',
  IN_REVIEW: 'En Revisión',
  IN_INCIDENCE: 'En Incidencia',
  IN_REPAIR: 'En Reparación',
  RESOLVED: 'Resuelto',
  CLOSED: 'Cerrado',
}

const PRIORITY_LABELS = {
  LOW: 'Baja',
  MEDIUM: 'Media',
  HIGH: 'Alta',
  CRITICAL: 'Crítica',
}

const CATEGORY_LABELS = {
  COINS: 'Pago Monedas',
  BILLS: 'Pago Billetes',
  TPV: 'Pago Tarjeta',
  SCREEN_OFF: 'Pantalla Apagada',
  SCREEN_BREAK: 'Pantalla Rota',
  SCREEN_TOUCH: 'Táctil No Responde',
  PRINTER_TICKET: 'Impresora Ticket',
  KIOSC_DOOR: 'Kiosco Puerta',
  KIOSC_OFF: 'Kiosco No Enciende',
  OTHER: 'Otro',
}

const FILTER_TABS = [
  { key: 'ALL', label: 'Todos' },
  { key: 'OPEN', label: 'Abiertos' },
  { key: 'IN_REVIEW', label: 'En Revisión' },
  { key: 'IN_INCIDENCE', label: 'En Incidencia' },
  { key: 'IN_REPAIR', label: 'En Reparación' },
  { key: 'RESOLVED', label: 'Resueltos' },
  { key: 'CLOSED', label: 'Cerrados' },
]

class SupportTicketsView extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      activeFilter: 'ALL',
      closeConfirmShown: false,
      closeId: null,
      detailModalOpen: false,
      detailId: null,
      deleteConfirmShown: false,
      deleteId: null,
      actuationText: '',
      showActuationModal: false,
      actuationAction: null, // 'resolve' or 'incidence'
      reviewPriority: '', // Priority selected for review action
      now: new Date(), // for live time elapsed updates
    }

    this.handleDownload = this.handleDownload.bind(this)
    this.confirmClose = this.confirmClose.bind(this)
    this.confirmDelete = this.confirmDelete.bind(this)
    this.closeDetailModal = this.closeDetailModal.bind(this)
    this.handleActuationSubmit = this.handleActuationSubmit.bind(this)
  }

  componentDidMount() {
    this.props.fetchSupportTickets('', '', '', '', '', '')
    // Update time every 60 seconds for live elapsed time
    this._timer = setInterval(() => this.setState({ now: new Date() }), 60000)
  }

  componentWillUnmount() {
    if (this._timer) clearInterval(this._timer)
  }

  componentDidUpdate(prevProps) {
    if (this.props.supportticketNeedRefresh && !prevProps.supportticketNeedRefresh) {
      this.props.fetchSupportTickets('', '', '', '', '', '')
      // also refresh the open detail if any
      if (this.state.detailId) {
        this.props.fetchSupportTicket(this.state.detailId)
      }
    }
  }

  getStatusCounts() {
    const { supporttickets } = this.props
    if (!supporttickets) return {}
    return supporttickets.reduce((acc, t) => {
      acc[t.Status] = (acc[t.Status] || 0) + 1
      return acc
    }, {})
  }

  /**
   * Format a millisecond duration into a human-readable string.
   */
  formatDuration(ms) {
    if (ms <= 0) return '0m'
    const days = Math.floor(ms / (1000 * 60 * 60 * 24))
    const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60))
    let text = ''
    if (days > 0) text += `${days}d `
    if (hours > 0 || days > 0) text += `${hours}h `
    text += `${minutes}m`
    return text.trim()
  }

  /**
   * Get severity class based on elapsed days
   */
  getTimerSeverity(ms) {
    const days = ms / (1000 * 60 * 60 * 24)
    if (days >= 3) return 'critical'
    if (days >= 1) return 'warning'
    return 'normal'
  }

  /**
   * Calculate time counters for each status transition.
   * Returns an array of { key, icon, label, time, active, severity }
   * - active: true if the ticket has reached/passed this status
   * - time: formatted duration between the two timestamps
   *
   * Workflow: OPEN → IN_REVIEW → RESOLVED | IN_INCIDENCE → IN_REPAIR → CLOSED
   */
  getStatusTimers(ticket) {
    const now = this.state.now
    const openAt = ticket.OpenAt ? new Date(ticket.OpenAt) : null
    const reviewedAt = ticket.ReviewedAt ? new Date(ticket.ReviewedAt) : null
    const resolvedAt = ticket.ResolvedAt ? new Date(ticket.ResolvedAt) : null
    const inIncidenceAt = ticket.InIncidenceAt ? new Date(ticket.InIncidenceAt) : null
    const inRepairAt = ticket.InRepairAt ? new Date(ticket.InRepairAt) : null
    const closedAt = ticket.ClosedAt ? new Date(ticket.ClosedAt) : null

    const STATUS_ORDER = ['OPEN', 'IN_REVIEW', 'IN_INCIDENCE', 'IN_REPAIR', 'RESOLVED', 'CLOSED']
    const currentIdx = STATUS_ORDER.indexOf(ticket.Status)

    const timers = []

    // 1) Apertura → Revisión
    const t1End = reviewedAt || (ticket.Status === 'OPEN' ? now : null)
    const t1Ms = openAt && t1End ? t1End - openAt : 0
    const t1Active = currentIdx >= 0 // always active since always open
    timers.push({
      key: 'open_to_review',
      icon: 'fas fa-folder-open',
      label: 'Abierto',
      time: this.formatDuration(Math.max(t1Ms, 0)),
      ms: Math.max(t1Ms, 0),
      active: t1Active,
      isLive: ticket.Status === 'OPEN',
    })

    // 2) Revisión → Resolución/Incidencia
    const t2Start = reviewedAt
    const t2End = resolvedAt || inIncidenceAt || (ticket.Status === 'IN_REVIEW' ? now : null)
    const t2Ms = t2Start && t2End ? t2End - t2Start : 0
    const t2Active = currentIdx >= 1 // IN_REVIEW or beyond
    timers.push({
      key: 'review_to_next',
      icon: 'fas fa-eye',
      label: 'En Revisión',
      time: this.formatDuration(Math.max(t2Ms, 0)),
      ms: Math.max(t2Ms, 0),
      active: t2Active,
      isLive: ticket.Status === 'IN_REVIEW',
    })

    // 3) Incidencia → Reparación/Resolución
    const t3Start = inIncidenceAt
    const t3End = inRepairAt || resolvedAt || (ticket.Status === 'IN_INCIDENCE' ? now : null)
    const t3Ms = t3Start && t3End ? t3End - t3Start : 0
    const t3Active = !!inIncidenceAt
    timers.push({
      key: 'incidence',
      icon: 'fas fa-exclamation-triangle',
      label: 'En Incidencia',
      time: this.formatDuration(Math.max(t3Ms, 0)),
      ms: Math.max(t3Ms, 0),
      active: t3Active,
      isLive: ticket.Status === 'IN_INCIDENCE',
    })

    // 4) Reparación
    const t4Start = inRepairAt
    const t4End = resolvedAt || (ticket.Status === 'IN_REPAIR' ? now : null)
    const t4Ms = t4Start && t4End ? t4End - t4Start : 0
    const t4Active = !!inRepairAt
    timers.push({
      key: 'repair',
      icon: 'fas fa-wrench',
      label: 'En Reparación',
      time: this.formatDuration(Math.max(t4Ms, 0)),
      ms: Math.max(t4Ms, 0),
      active: t4Active,
      isLive: ticket.Status === 'IN_REPAIR',
    })

    // 5) Resolución → Cierre
    const t5Start = resolvedAt
    const t5End = closedAt || (ticket.Status === 'RESOLVED' ? now : null)
    const t5Ms = t5Start && t5End ? t5End - t5Start : 0
    const t5Active = !!resolvedAt
    timers.push({
      key: 'resolved',
      icon: 'fas fa-check-circle',
      label: 'Resuelto',
      time: this.formatDuration(Math.max(t5Ms, 0)),
      ms: Math.max(t5Ms, 0),
      active: t5Active,
      isLive: ticket.Status === 'RESOLVED',
    })

    // 6) Total (OpenAt → last known timestamp or now)
    const lastTs = closedAt || resolvedAt || inRepairAt || inIncidenceAt || reviewedAt || now
    const totalMs = openAt ? lastTs - openAt : 0
    timers.push({
      key: 'total',
      icon: 'fas fa-hourglass-half',
      label: 'Total',
      time: this.formatDuration(Math.max(totalMs, 0)),
      ms: Math.max(totalMs, 0),
      active: true,
      isLive: ticket.Status !== 'CLOSED',
      isTotal: true,
    })

    return timers
  }

  getCategoryCounts() {
    const { supporttickets } = this.props
    if (!supporttickets) return {}
    return supporttickets.reduce((acc, t) => {
      acc[t.Category] = (acc[t.Category] || 0) + 1
      return acc
    }, {})
  }

  getPriorityCounts() {
    const { supporttickets } = this.props
    if (!supporttickets) return {}
    return supporttickets.reduce((acc, t) => {
      acc[t.Priority] = (acc[t.Priority] || 0) + 1
      return acc
    }, {})
  }

  getKPIs() {
    const { supporttickets } = this.props
    if (!supporttickets) return { pendingCount: 0, resolvedToday: 0, avgResponseTime: 0, avgResolutionTime: 0, criticalOpen: 0, escalatedCount: 0 }

    const now = new Date()
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    let pendingCount = 0
    let resolvedToday = 0
    let criticalOpen = 0
    let escalatedCount = 0
    let totalResponseTime = 0
    let responseTimeCount = 0
    let totalResolutionTime = 0
    let resolutionTimeCount = 0

    supporttickets.forEach(t => {
      // Pending: not resolved or closed
      if (t.Status !== 'RESOLVED' && t.Status !== 'CLOSED') {
        pendingCount++
      }

      // Critical open: high/critical priority AND not resolved/closed
      if ((t.Priority === 'CRITICAL' || t.Priority === 'HIGH') && t.Status !== 'RESOLVED' && t.Status !== 'CLOSED') {
        criticalOpen++
      }

      // Escalated: tickets that went through IN_INCIDENCE or IN_REPAIR
      if (t.InIncidenceAt || t.InRepairAt) {
        escalatedCount++
      }

      // Resolved today
      if ((t.Status === 'RESOLVED' || t.Status === 'CLOSED') && t.ResolvedAt) {
        if (new Date(t.ResolvedAt) >= todayStart) {
          resolvedToday++
        }
      }

      // Avg response time: OpenAt → ReviewedAt
      if (t.ReviewedAt && t.OpenAt) {
        const diffH = (new Date(t.ReviewedAt) - new Date(t.OpenAt)) / (1000 * 60 * 60)
        if (diffH > 0 && diffH < 2000) {
          totalResponseTime += diffH
          responseTimeCount++
        }
      }

      // Avg resolution time: OpenAt → ResolvedAt
      if (t.ResolvedAt && t.OpenAt) {
        const diffH = (new Date(t.ResolvedAt) - new Date(t.OpenAt)) / (1000 * 60 * 60)
        if (diffH > 0 && diffH < 5000) {
          totalResolutionTime += diffH
          resolutionTimeCount++
        }
      }
    })

    return {
      pendingCount,
      resolvedToday,
      avgResponseTime: responseTimeCount > 0 ? Math.round(totalResponseTime / responseTimeCount * 10) / 10 : 0,
      avgResolutionTime: resolutionTimeCount > 0 ? Math.round(totalResolutionTime / resolutionTimeCount * 10) / 10 : 0,
      criticalOpen,
      escalatedCount,
    }
  }

  getFilteredTickets() {
    const { supporttickets } = this.props
    const { activeFilter } = this.state
    if (!supporttickets) return []
    if (activeFilter === 'ALL') return supporttickets
    return supporttickets.filter(t => t.Status === activeFilter)
  }

  handleDownload() {
    const { supporttickets } = this.props
    if (!supporttickets || supporttickets.length === 0) return

    const headers = ['#', 'Fecha Apertura', 'Fecha Cierre', 'Cliente', 'Máquina', 'Categoría', 'Descripción', 'Actuación', 'Estado', 'Prioridad', 'Ejecutor', 'Cerrado por']
    const rows = supporttickets.map(t => [
      t.number,
      new Date(t.OpenAt).toLocaleString(),
      t.ClosedAt ? new Date(t.ClosedAt).toLocaleString() : '',
      t.Customer && t.Customer.Fullname ? t.Customer.Fullname : 'VACWAY',
      t.Machine ? t.Machine.Serial : 'Todas las máquinas',
      t.Category,
      t.Description ? t.Description.replace(/[\n\r,;]/g, ' ') : '',
      t.Actuation ? t.Actuation.replace(/[\n\r,;]/g, ' ') : '',
      t.Status,
      t.Priority,
      t.Executor ? t.Executor.fullname : '',
      t.ClosedBy ? t.ClosedBy.fullname : ''
    ])

    const csvContent = [headers, ...rows].map(row =>
      row.map(cell => `"${cell}"`).join(';')
    ).join('\n')

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `tickets_soporte_${new Date().toISOString().substring(0, 10)}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  goTo(id) {
    this.setState({ detailModalOpen: true, detailId: id })
    this.props.fetchSupportTicket(id)
  }

  closeDetailModal() {
    this.setState({ detailModalOpen: false, detailId: null, actuationText: '', reviewPriority: '' })
  }

  handleReviewTicket(id, e) {
    if (e) e.stopPropagation()
    const { reviewPriority } = this.state
    if (!reviewPriority) return
    this.props.reviewTicketSupport(id, reviewPriority)
    this.setState({ reviewPriority: '' })
  }

  // Open actuation modal for resolve action
  handleOpenResolveModal(id, e) {
    if (e) e.stopPropagation()
    this.setState({ 
      showActuationModal: true, 
      actuationAction: 'resolve',
      actuationText: ''
    })
  }

  // Open actuation modal for incidence action
  handleOpenIncidenceModal(id, e) {
    if (e) e.stopPropagation()
    this.setState({ 
      showActuationModal: true, 
      actuationAction: 'incidence',
      actuationText: ''
    })
  }

  handleActuationSubmit() {
    const { actuationAction, actuationText, detailId } = this.state
    
    if (!actuationText.trim()) {
      alert('Por favor, introduce el texto de actuación')
      return
    }

    if (actuationAction === 'resolve') {
      this.props.resolveTicketSupport(detailId, actuationText)
    } else if (actuationAction === 'incidence') {
      this.props.incidenceTicketSupport(detailId, actuationText)
    }

    this.setState({ 
      showActuationModal: false, 
      actuationAction: null, 
      actuationText: '' 
    })
  }

  handleRepairTicket(id, e) {
    if (e) e.stopPropagation()
    this.props.repairTicketSupport(id)
  }

  handleCloseTicket(id, e) {
    if (e) e.stopPropagation()
    this.setState({ closeConfirmShown: true, closeId: id })
  }

  confirmClose() {
    this.setState({ closeConfirmShown: false })
    this.props.closeTicketSupport(this.state.closeId)
  }

  handleDeleteTicket(id, e) {
    if (e) e.stopPropagation()
    this.setState({ deleteConfirmShown: true, deleteId: id })
  }

  confirmDelete() {
    const id = this.state.deleteId
    this.setState({ deleteConfirmShown: false, deleteId: null })
    this.props.deleteSupportTicket(id)
    // close detail modal if open
    if (this.state.detailId === id) {
      this.closeDetailModal()
    }
  }

  renderTicketCard(ticket) {
    const statusLabel = STATUS_LABELS[ticket.Status] || ticket.Status
    const priorityLabel = PRIORITY_LABELS[ticket.Priority] || ticket.Priority
    const categoryLabel = CATEGORY_LABELS[ticket.Category] || ticket.Category
    const timers = this.getStatusTimers(ticket)

    return (
      <div className="st-admin-ticket-card" key={ticket._id}
        onClick={() => this.goTo(ticket._id)}>
        <div className="st-admin-ticket-row">
          <div className={`st-admin-status-bar ${ticket.Status}`}></div>
          <div className="st-admin-ticket-content">
            <div className="st-admin-ticket-info">
              <div className="st-admin-ticket-number">#{ticket.number}</div>
              <div className="st-admin-ticket-meta">
                <span><i className="fas fa-calendar-alt"></i>
                  {new Date(ticket.OpenAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
                <span><i className="fas fa-tag"></i>{categoryLabel}</span>
              </div>
            </div>

            <div className="st-admin-ticket-context">
              <div className="st-admin-context-item">
                <i className="fas fa-building"></i>
                <strong>{ticket.Customer && ticket.Customer.Fullname ? ticket.Customer.Fullname : 'VACWAY'}</strong>
              </div>
              <div className="st-admin-context-item">
                <i className="fas fa-hdd"></i>
                {ticket.Machine && ticket.Machine.Serial ? ticket.Machine.Serial : 'Todas las máquinas'}
              </div>
              {ticket.Executor &&
                <div className="st-admin-context-item">
                  <i className="fas fa-user-cog"></i>
                  {ticket.Executor.fullname}
                </div>
              }
            </div>

            <div className="st-admin-ticket-desc">
              {ticket.Description
                ? <span className="desc-text">{ticket.Description}</span>
                : <span className="desc-empty">Sin descripción</span>
              }
            </div>

            {/* Status & Priority badges */}
            <div className="st-admin-ticket-badges">
              <span className={`ticket-status-badge ${ticket.Status}`}>
                {statusLabel}
              </span>
              <span className={`ticket-priority-badge ${ticket.Priority}`}>
                {priorityLabel}
              </span>
            </div>

            {/* Time counters column */}
            <div className="st-admin-ticket-timers">
              {timers.map(t => (
                <div key={t.key}
                  className={`st-timer-chip ${t.active ? this.getTimerSeverity(t.ms) : 'disabled'} ${t.isLive ? 'live' : ''} ${t.isTotal ? 'total' : ''}`}>
                  <i className={t.icon}></i>
                  <span className="st-timer-value">{t.active ? t.time : '--'}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderDetailModal() {
    const { supportticket, role, type } = this.props
    const { detailModalOpen, showActuationModal, actuationAction, actuationText, reviewPriority } = this.state

    if (!detailModalOpen) return null

    const isLoading = !supportticket
    const isAdmin = role === 'ADMIN'
    const isMaster = type === 'MASTER'
    const isClosed = supportticket && supportticket.Status === 'CLOSED'

    return (
      <>
        <Modal isOpen={detailModalOpen} toggle={this.closeDetailModal}
          className="st-detail-modal" size="xl">
          <ModalHeader toggle={this.closeDetailModal} className="st-detail-modal-header">
            {supportticket
              ? <span><i className="fas fa-ticket-alt"></i> Ticket #{supportticket.number}</span>
              : <span><i className="fas fa-ticket-alt"></i> Cargando...</span>
            }
          </ModalHeader>
          <ModalBody className="st-detail-modal-body">
            {isLoading ? <LoadingProgress /> : (
              <>
                {/* Badges */}
                <div className="st-modal-badges-row">
                  <span className={`ticket-status-badge ${supportticket.Status}`}>
                    {STATUS_LABELS[supportticket.Status] || supportticket.Status}
                  </span>
                  <span className={`ticket-priority-badge ${supportticket.Priority}`}>
                    {PRIORITY_LABELS[supportticket.Priority] || supportticket.Priority}
                  </span>
                </div>

                {/* Time counters */}
                <div className="st-modal-timers-row">
                  {this.getStatusTimers(supportticket).map(t => (
                    <div key={t.key}
                      className={`st-modal-timer-item ${t.active ? this.getTimerSeverity(t.ms) : 'disabled'} ${t.isLive ? 'live' : ''} ${t.isTotal ? 'total' : ''}`}>
                      <div className="st-modal-timer-icon"><i className={t.icon}></i></div>
                      <div className="st-modal-timer-label">{t.label}</div>
                      <div className="st-modal-timer-value">{t.active ? t.time : '--'}</div>
                    </div>
                  ))}
                </div>

                {/* Info grid */}
                <div className="st-modal-detail-grid">
                  <div className="st-detail-card">
                    <div className="st-detail-card-title">
                      <i className="fas fa-info-circle"></i> Información General
                    </div>
                    <div className="st-detail-card-body">
                      <div className="st-detail-field">
                        <span className="st-detail-field-label">Número</span>
                        <span className="st-detail-field-value">#{supportticket.number}</span>
                      </div>
                      <div className="st-detail-field">
                        <span className="st-detail-field-label">Categoría</span>
                        <span className="st-detail-field-value">{CATEGORY_LABELS[supportticket.Category] || supportticket.Category}</span>
                      </div>
                      <div className="st-detail-field">
                        <span className="st-detail-field-label">Cliente</span>
                        <span className="st-detail-field-value">{supportticket.Customer && supportticket.Customer.Fullname ? supportticket.Customer.Fullname : 'VACWAY'}</span>
                      </div>
                      <div className="st-detail-field">
                        <span className="st-detail-field-label">Máquina</span>
                        <span className="st-detail-field-value">{supportticket.Machine && supportticket.Machine.Serial ? supportticket.Machine.Serial : 'Todas las máquinas'}</span>
                      </div>
                      <div className="st-detail-field">
                        <span className="st-detail-field-label">Creado por</span>
                        <span className="st-detail-field-value">{supportticket.User ? supportticket.User.fullname : 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="st-detail-card">
                    <div className="st-detail-card-title">
                      <i className="fas fa-calendar"></i> Fechas y Asignaciones
                    </div>
                    <div className="st-detail-card-body">
                      <div className="st-detail-field">
                        <span className="st-detail-field-label">Fecha Apertura</span>
                        <span className="st-detail-field-value">{new Date(supportticket.OpenAt).toLocaleString()}</span>
                      </div>
                      {supportticket.Executor &&
                        <div className="st-detail-field">
                          <span className="st-detail-field-label">Ejecutor</span>
                          <span className="st-detail-field-value">{supportticket.Executor.fullname}</span>
                        </div>
                      }
                      {supportticket.ReviewedAt &&
                        <div className="st-detail-field">
                          <span className="st-detail-field-label">En Revisión</span>
                          <span className="st-detail-field-value">
                            {new Date(supportticket.ReviewedAt).toLocaleString()}
                            {supportticket.ReviewedBy ? ` — ${supportticket.ReviewedBy.fullname}` : ''}
                          </span>
                        </div>
                      }
                      {supportticket.InIncidenceAt &&
                        <div className="st-detail-field">
                          <span className="st-detail-field-label">En Incidencia</span>
                          <span className="st-detail-field-value">
                            {new Date(supportticket.InIncidenceAt).toLocaleString()}
                            {supportticket.InIncidenceBy ? ` — ${supportticket.InIncidenceBy.fullname}` : ''}
                          </span>
                        </div>
                      }
                      {supportticket.InRepairAt &&
                        <div className="st-detail-field">
                          <span className="st-detail-field-label">En Reparación</span>
                          <span className="st-detail-field-value">
                            {new Date(supportticket.InRepairAt).toLocaleString()}
                            {supportticket.InRepairBy ? ` — ${supportticket.InRepairBy.fullname}` : ''}
                          </span>
                        </div>
                      }
                      {supportticket.ResolvedAt &&
                        <div className="st-detail-field">
                          <span className="st-detail-field-label">Resuelto</span>
                          <span className="st-detail-field-value">
                            {new Date(supportticket.ResolvedAt).toLocaleString()}
                            {supportticket.ResolvedBy ? ` — ${supportticket.ResolvedBy.fullname}` : ''}
                          </span>
                        </div>
                      }
                      {supportticket.ClosedAt &&
                        <div className="st-detail-field">
                          <span className="st-detail-field-label">Cerrado</span>
                          <span className="st-detail-field-value">
                            {new Date(supportticket.ClosedAt).toLocaleString()}
                            {supportticket.ClosedBy ? ` — ${supportticket.ClosedBy.fullname}` : ''}
                          </span>
                        </div>
                      }
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="st-detail-card" style={{ marginBottom: 16 }}>
                  <div className="st-detail-card-title">
                    <i className="fas fa-file-alt"></i> Descripción
                  </div>
                  <div className="st-detail-card-body">
                    <div className="st-detail-description">
                      {supportticket.Description || 'Sin descripción'}
                    </div>
                  </div>
                </div>

                {/* Actuation */}
                {supportticket.Actuation &&
                  <div className="st-detail-card" style={{ marginBottom: 16 }}>
                    <div className="st-detail-card-title">
                      <i className="fas fa-tools"></i> Actuación Realizada
                    </div>
                    <div className="st-detail-card-body">
                      <div className="st-detail-description">
                        {supportticket.Actuation}
                      </div>
                    </div>
                  </div>
                }

                {supportticket.Observations &&
                  <div className="st-detail-card" style={{ marginBottom: 16 }}>
                    <div className="st-detail-card-title">
                      <i className="fas fa-sticky-note"></i> Observaciones
                    </div>
                    <div className="st-detail-card-body">
                      <div className="st-detail-description">
                        {supportticket.Observations}
                      </div>
                    </div>
                  </div>
                }

                {/* Actions - New workflow */}
                {isAdmin && !isClosed &&
                  <div className="st-detail-actions-bar">
                    <h3><i className="fas fa-cogs"></i> Acciones</h3>
                    <div className="st-detail-actions-row justify-content-center">
                      {/* OPEN -> Priority select + Review button (disabled until priority selected) */}
                      {supportticket.Status === 'OPEN' &&
                        <div className="st-review-action-group">
                          <select
                            className="st-review-priority-select"
                            value={reviewPriority}
                            onChange={e => this.setState({ reviewPriority: e.target.value })}
                          >
                            <option value="">Seleccionar prioridad...</option>
                            <option value="LOW">Baja</option>
                            <option value="MEDIUM">Media</option>
                            <option value="HIGH">Alta</option>
                            <option value="CRITICAL">Crítica</option>
                          </select>
                          <button
                            className="st-detail-action-btn review"
                            disabled={!reviewPriority}
                            onClick={() => this.handleReviewTicket(supportticket._id)}
                          >
                            <i className="fas fa-eye"></i>Revisar
                          </button>
                        </div>
                      }
                      {/* IN_REVIEW -> Resolve or Incidence */}
                      {supportticket.Status === 'IN_REVIEW' &&
                        <>
                          <button className="st-detail-action-btn resolve"
                            onClick={() => this.handleOpenResolveModal(supportticket._id)}>
                            <i className="fas fa-check"></i>Resolver
                          </button>
                          <button className="st-detail-action-btn incidence"
                            onClick={() => this.handleOpenIncidenceModal(supportticket._id)}>
                            <i className="fas fa-exclamation-triangle"></i>Poner en Incidencia
                          </button>
                        </>
                      }
                      {/* IN_INCIDENCE -> No repair button (managed through incidences view) */}
                      {/* Close button - only for MASTER */}
                      {isMaster &&
                        <button className="st-detail-action-btn close-action"
                          onClick={() => this.handleCloseTicket(supportticket._id)}>
                          <i className="fas fa-times"></i>Cerrar
                        </button>
                      }
                    </div>
                  </div>
                }

                {/* Delete button - only for ADMIN MASTER */}
                {isAdmin && isMaster &&
                  <div className="st-detail-delete-row justify-content-center">
                    <button className="st-detail-delete-btn"
                      onClick={() => this.handleDeleteTicket(supportticket._id)}>
                      <i className="fas fa-trash"></i>Eliminar Ticket
                    </button>
                  </div>
                }
              </>
            )}
          </ModalBody>
        </Modal>

        {/* Actuation Modal for Resolve/Incidence */}
        <Modal isOpen={showActuationModal} toggle={() => this.setState({ showActuationModal: false, actuationText: '' })}>
          <ModalHeader toggle={() => this.setState({ showActuationModal: false, actuationText: '' })}>
            {actuationAction === 'resolve' ? 'Resolver Ticket' : 'Poner en Incidencia'}
          </ModalHeader>
          <ModalBody>
            <p>
              {actuationAction === 'resolve' 
                ? 'Describe la actuación realizada para resolver el ticket:' 
                : 'Describe el motivo por el que se pone en incidencia:'}
            </p>
            <textarea
              className="form-control"
              rows={4}
              value={actuationText}
              onChange={(e) => this.setState({ actuationText: e.target.value })}
              placeholder="Escribe la actuación realizada..."
            />
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={this.handleActuationSubmit}>
              {actuationAction === 'resolve' ? 'Resolver' : 'Poner en Incidencia'}
            </Button>
            <Button color="secondary" onClick={() => this.setState({ showActuationModal: false, actuationText: '' })}>
              Cancelar
            </Button>
          </ModalFooter>
        </Modal>
      </>
    )
  }

  renderSummaryPanel() {
    const { supporttickets } = this.props
    const counts = this.getStatusCounts()
    const categoryCounts = this.getCategoryCounts()
    const priorityCounts = this.getPriorityCounts()
    const total = supporttickets ? supporttickets.length : 0

    return (
      <div className="st-admin-summary-panel">
        {/* Status summary */}
        <div className="st-summary-card">
          <div className="st-summary-card-header">
            <h3><i className="fas fa-chart-pie"></i> Resumen por Estado</h3>
          </div>
          <div className="st-summary-card-body">
            <div className="st-summary-total">{total} tickets totales</div>
            <div className="st-summary-table">
              {['OPEN', 'IN_REVIEW', 'IN_INCIDENCE', 'IN_REPAIR', 'RESOLVED', 'CLOSED'].map(status => {
                const count = counts[status] || 0
                const pct = total > 0 ? Math.round((count / total) * 100) : 0
                return (
                  <div className="st-summary-row" key={status}
                    onClick={() => this.setState({ activeFilter: status })}>
                    <div className="st-summary-row-left">
                      <span className={`st-summary-dot ${status}`}></span>
                      <span className="st-summary-label">{STATUS_LABELS[status]}</span>
                    </div>
                    <div className="st-summary-row-right">
                      <span className="st-summary-count">{count}</span>
                      <div className="st-summary-bar-bg">
                        <div className={`st-summary-bar-fill ${status}`} style={{ width: `${pct}%` }}></div>
                      </div>
                      <span className="st-summary-pct">{pct}%</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Priority summary */}
        <div className="st-summary-card">
          <div className="st-summary-card-header">
            <h3><i className="fas fa-exclamation-triangle"></i> Por Prioridad</h3>
          </div>
          <div className="st-summary-card-body">
            <div className="st-summary-table">
              {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(priority => {
                const count = priorityCounts[priority] || 0
                return (
                  <div className="st-summary-row" key={priority}>
                    <div className="st-summary-row-left">
                      <span className={`ticket-priority-badge ${priority}`} style={{ fontSize: 10 }}>
                        {PRIORITY_LABELS[priority]}
                      </span>
                    </div>
                    <div className="st-summary-row-right">
                      <span className="st-summary-count">{count}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* Category summary */}
        <div className="st-summary-card">
          <div className="st-summary-card-header">
            <h3><i className="fas fa-tags"></i> Por Categoría</h3>
          </div>
          <div className="st-summary-card-body">
            <div className="st-summary-table">
              {Object.entries(categoryCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([cat, count]) => (
                  <div className="st-summary-row" key={cat}>
                    <div className="st-summary-row-left">
                      <span className="st-summary-label">{CATEGORY_LABELS[cat] || cat}</span>
                    </div>
                    <div className="st-summary-row-right">
                      <span className="st-summary-count">{count}</span>
                    </div>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      </div>
    )
  }

  render() {
    const { role, supporttickets, error, loading } = this.props
    const { activeFilter } = this.state
    const counts = this.getStatusCounts()
    const filteredTickets = this.getFilteredTickets()
    const kpis = this.getKPIs()

    return (
      <div className="support-ticket-admin-view">
        {/* Header */}
        <div className="st-admin-header">
          <div className="header-content">
            <div className="header-text">
              <h1>
                <i className="fas fa-headset mr-3"></i>
                Tickets de Soporte
              </h1>
            </div>
            <div className="header-actions">
              {role === 'ADMIN' &&
                <button className="st-admin-download-btn" onClick={this.handleDownload}>
                  <i className="fas fa-file-excel"></i>Descargar CSV
                </button>
              }
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        {supporttickets && !loading && (
          <div className="st-kpi-row">
            <div className="st-kpi-card pending">
              <div className="kpi-icon"><i className="fas fa-clock"></i></div>
              <div className="kpi-content">
                <div className="kpi-value">{kpis.pendingCount}</div>
                <div className="kpi-label">Pendientes</div>
              </div>
            </div>
            <div className="st-kpi-card critical">
              <div className="kpi-icon"><i className="fas fa-fire"></i></div>
              <div className="kpi-content">
                <div className="kpi-value">{kpis.criticalOpen}</div>
                <div className="kpi-label">Alta/Crítica Abiertos</div>
              </div>
            </div>
            <div className="st-kpi-card resolved">
              <div className="kpi-icon"><i className="fas fa-check-circle"></i></div>
              <div className="kpi-content">
                <div className="kpi-value">{kpis.resolvedToday}</div>
                <div className="kpi-label">Resueltos Hoy</div>
              </div>
            </div>
            <div className="st-kpi-card response">
              <div className="kpi-icon"><i className="fas fa-bolt"></i></div>
              <div className="kpi-content">
                <div className="kpi-value">{kpis.avgResponseTime}h</div>
                <div className="kpi-label">Tiempo Respuesta Medio</div>
              </div>
            </div>
            <div className="st-kpi-card resolution">
              <div className="kpi-icon"><i className="fas fa-stopwatch"></i></div>
              <div className="kpi-content">
                <div className="kpi-value">{kpis.avgResolutionTime}h</div>
                <div className="kpi-label">Tiempo Resolución Medio</div>
              </div>
            </div>
            <div className="st-kpi-card escalated">
              <div className="kpi-icon"><i className="fas fa-level-up-alt"></i></div>
              <div className="kpi-content">
                <div className="kpi-value">{kpis.escalatedCount}</div>
                <div className="kpi-label">Escalados</div>
              </div>
            </div>
          </div>
        )}

        {/* Split layout - 2 columns below header */}
        <div className="st-admin-split">
          {/* LEFT — Filter + Ticket List */}
          <div className="st-admin-list-panel">
            {/* Filter */}
            <SupportTicketsFilterForm />
            
            <div className="ticket-filter-tabs">
              {FILTER_TABS.map(tab => (
                <button key={tab.key}
                  className={`ticket-filter-tab ${activeFilter === tab.key ? 'active' : ''}`}
                  onClick={() => this.setState({ activeFilter: tab.key })}>
                  {tab.label}
                  {tab.key !== 'ALL' && counts[tab.key] > 0 &&
                    <span style={{ marginLeft: 4 }}>({counts[tab.key]})</span>
                  }
                </button>
              ))}
            </div>

            {error ? (
              <ErrorMessage message={error.message} />
            ) : !supporttickets || loading ? (
              <LoadingProgress />
            ) : (
              <div className="st-admin-ticket-list">
                {filteredTickets.length > 0
                  ? filteredTickets.map(ticket => this.renderTicketCard(ticket))
                  : (
                    <div className="st-admin-empty">
                      <i className="fas fa-inbox"></i>
                      <p>No hay tickets {activeFilter !== 'ALL' ? `con estado "${STATUS_LABELS[activeFilter]}"` : ''}</p>
                    </div>
                  )
                }
              </div>
            )}
          </div>

          {/* RIGHT — Summary Panel */}
          {supporttickets && !loading && this.renderSummaryPanel()}
        </div>

        {/* Confirm close modal */}
        <Modal isOpen={this.state.closeConfirmShown}
          toggle={() => this.setState({ closeConfirmShown: false })}>
          <ModalHeader toggle={() => this.setState({ closeConfirmShown: false })}>
            Confirmar Cierre
          </ModalHeader>
          <ModalBody>
            <p>¿Estás seguro de cerrar el ticket #{this.state.closeId}?</p>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" className="mr-1" onClick={this.confirmClose}>Sí</Button>
            <Button color="secondary" onClick={() => this.setState({ closeConfirmShown: false })}>No</Button>
          </ModalFooter>
        </Modal>

        {/* Confirm delete modal */}
        <Modal isOpen={this.state.deleteConfirmShown}
          toggle={() => this.setState({ deleteConfirmShown: false })}>
          <ModalHeader toggle={() => this.setState({ deleteConfirmShown: false })}>
            Confirmar Eliminación
          </ModalHeader>
          <ModalBody>
            <p>¿Estás seguro de eliminar permanentemente el ticket #{this.state.deleteId}?</p>
          </ModalBody>
          <ModalFooter>
            <Button color="danger" onClick={this.confirmDelete}>Eliminar</Button>
            <Button color="secondary" onClick={() => this.setState({ deleteConfirmShown: false })}>Cancelar</Button>
          </ModalFooter>
        </Modal>

        {/* Ticket detail modal */}
        {this.renderDetailModal()}
      </div>
    )
  }
}

const mapStateToProps = state => ({
  supporttickets: state.supporttickets.supporttickets,
  supportticket: state.supporttickets.supportticket,
  error: state.supporttickets.error,
  role: state.auth.role,
  type: state.auth.type,
  loading: state.supporttickets.loading,
  supportticketNeedRefresh: state.supporttickets.supportticketNeedRefresh,
  form: state.form
})

const mapDispatchToProps = dispatch => ({
  fetchSupportTickets: bindActionCreators(fetchSupportTickets, dispatch),
  fetchSupportTicket: bindActionCreators(fetchSupportTicket, dispatch),
  closeTicketSupport: bindActionCreators(closeTicketSupport, dispatch),
  reviewTicketSupport: bindActionCreators(reviewTicketSupport, dispatch),
  resolveTicketSupport: bindActionCreators(resolveTicketSupport, dispatch),
  incidenceTicketSupport: bindActionCreators(incidenceTicketSupport, dispatch),
  repairTicketSupport: bindActionCreators(repairTicketSupport, dispatch),
  deleteSupportTicket: bindActionCreators(deleteSupportTicket, dispatch)
})

export default connect(mapStateToProps, mapDispatchToProps)(SupportTicketsView)
